import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getEmailVisibilitySetting, updateEmailVisibilitySetting } from "../service/apiService";
import { useTheme, ThemeMode } from "./ThemeContext";

// Add this utility function at the top (after imports)
const safeLocalStorage = {
  setItem: (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        console.log('localStorage full, cleaning up...');
        
        // Get all items except critical ones
        const criticalKeys = ['authToken', 'showEmailAddresses', 'userDetails', 'kyva.theme'];
        const itemsToClean = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && !criticalKeys.includes(key)) {
            const value = localStorage.getItem(key) || '';
            itemsToClean.push({
              key,
              size: value.length
            });
          }
        }
        
        // Remove largest non-critical items
        itemsToClean
          .sort((a, b) => b.size - a.size)
          .slice(0, 5) // Remove top 5 largest items
          .forEach(item => {
            localStorage.removeItem(item.key);
            console.log(`Removed ${item.key} to free space`);
          });
        
        // Try again
        try {
          localStorage.setItem(key, value);
          return true;
        } catch (retryError) {
          console.error('Still failed after cleanup:', retryError);
          return false;
        }
      }
      return false;
    }
  },
  
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error('Failed to read from localStorage:', e);
      return null;
    }
  }
};

// Extend the UserContextProps to include additional user details
interface UserContextProps {
  userId: string;
  setUserId: (userId: string) => void;
  userDetails: UserDetails | null;
  setUserDetails: (details: UserDetails) => void;
  userDisplayName: string;
  setUserDisplayName: (userDisplayName: string) => void;
  authenticated: boolean;
  setAuthenticated: (authenticated: boolean) => void;
  token: string | null;
  setToken: (token: string) => void;
  getToken: () => string | null;
  showEmailAddresses: boolean;
  setShowEmailAddresses: (show: boolean) => Promise<void>;
  isLoadingEmailSetting: boolean;
}

interface UserDetails {
  id: string;
  role: string;
  /** From SUPER_ADMIN_EMAILS on the api service; elevated project access. */
  is_super_admin?: boolean;
  /** Hours a super admin delete waits in Recently deleted before it is permanent. */
  deletion_retention_hours?: number;
  assigned_knowledge_base: string[];
  created_at: string;
  updated_at: string;
  last_login: string;
  user_status: string;
  short_term_memory_enabled: boolean;
  long_term_memory_enabled: boolean;
  theme?: ThemeMode;
  permissions?: {
    files?: {
      canDownload?: boolean;
      canPreview?: boolean;
    };
  };
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

// Existing UserProvider component updated to include userDetails and setUserDetails
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userId, setUserId] = useState<string>("");
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string>("");
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoadingEmailSetting, setIsLoadingEmailSetting] = useState<boolean>(true);
  
  const [showEmailAddressesState, setShowEmailAddressesState] = useState<boolean>(() => {
    const saved = safeLocalStorage.getItem("showEmailAddresses");
    // If no saved value exists, default to false (hide emails)
    if (saved === null) {
      return false; // Default to hiding emails for security
    }
    // Otherwise use the saved value
    return saved === "true";
  });

  // Fetch the email visibility setting when authenticated
  useEffect(() => {
    if (authenticated && userId) {
      fetchEmailVisibilitySetting();
    }
  }, [authenticated, userId]);

  // Hydrate the persisted theme preference from the user record once it loads.
  const { hydrateFromServer } = useTheme();
  useEffect(() => {
    const serverTheme = userDetails?.theme;
    if (serverTheme === "light" || serverTheme === "dark" || serverTheme === "system") {
      hydrateFromServer(serverTheme);
    }
  }, [userDetails?.theme, hydrateFromServer]);

  // Function to fetch the email visibility setting
  const fetchEmailVisibilitySetting = async () => {
    setIsLoadingEmailSetting(true);
    try {
      const response = await getEmailVisibilitySetting();
      setShowEmailAddressesState(response.showEmailAddresses);
      
      // Cache the setting for faster UI updates
      safeLocalStorage.setItem("showEmailAddresses", String(response.showEmailAddresses));
    } catch (error) {
      console.error("Error fetching email visibility setting:", error);
      
      // Fall back to cached value if API fails
      const cached = safeLocalStorage.getItem("showEmailAddresses");
      if (cached !== null) {
        setShowEmailAddressesState(cached === "true");
      }
    } finally {
      setIsLoadingEmailSetting(false);
    }
  };

  // Updated to use the API for changing the setting
  const setShowEmailAddresses = async (show: boolean) => {
    // Optimistically update UI
    setShowEmailAddressesState(show);
    
    // Update cache
    safeLocalStorage.setItem("showEmailAddresses", String(show));
    
    // Only admins should update the global setting
    if (userDetails?.role === 'admin') {
      try {
        await updateEmailVisibilitySetting(show);
        console.log(`Email visibility setting updated to ${show ? 'visible' : 'hidden'}`);
      } catch (error) {
        console.error("Failed to update email visibility setting:", error);
        
        // Revert state if API call fails
        setShowEmailAddressesState(!show);
        safeLocalStorage.setItem("showEmailAddresses", String(!show));
        
        // Re-throw for error handling in components
        throw error;
      }
    }
  };

  const setToken = (newToken: string) => {
    setTokenState(newToken);
    sessionStorage.setItem("authToken", newToken);
  };

  const getToken = () => {
    return token || sessionStorage.getItem("authToken");
  };

  return (
    <UserContext.Provider
      value={{
        userId,
        setUserId,
        userDetails,
        setUserDetails,
        userDisplayName,
        setUserDisplayName,
        authenticated,
        setAuthenticated,
        token,
        setToken,
        getToken,
        showEmailAddresses: showEmailAddressesState, 
        setShowEmailAddresses,
        isLoadingEmailSetting
      }}
    >
      {children}
    </UserContext.Provider>
  );
};